// Client for the console service (backend/firstmagic/console), the parent-facing
// API. Types mirror the response models in console/routes/.

// What the kid said — text is the speech-to-text transcript; audio_url is the
// ground truth recording.
export interface TurnUser {
  text: string;
  audio_url: string;
  image_url: string | null;
}

// What was answered — only the text that was spoken to the child.
export interface TurnModel {
  text: string;
}

// One exchange: the kid's input and the answer, rendered as two bubbles.
export interface Turn {
  id: number;
  // UTC ISO; day grouping happens client-side (lib/history.ts). When the
  // input arrived / when the spoken answer was ready — not DB insert time.
  input_received_at: string;
  output_received_at: string;
  user: TurnUser;
  model: TurnModel;
}

export interface TurnsPage {
  items: Turn[]; // newest first
  // Pass back as cursor to fetch the page of older turns; null = exhausted.
  next_cursor: string | null;
}

// A device linked to the signed-in parent's account (console/routes/devices.py).
export interface LinkedDevice {
  device_id: string;
  name: string; // display name, chosen at link time, renameable
  linked_at: string; // UTC ISO
}

// The origin every API is served from — the dev load balancer, the public api
// host in production. NEXT_PUBLIC_: inlined at build time; the app is a pure
// client (static export), so every request is a cross-origin browser call.
function apiOrigin(): string {
  const url = process.env.NEXT_PUBLIC_API_ORIGIN;
  if (!url) throw new Error("NEXT_PUBLIC_API_ORIGIN is not set");
  return url;
}

// Thrown for non-2xx console responses so callers can branch on status (the
// link form turns 404/409 into inline field errors).
export class ConsoleError extends Error {
  constructor(
    public readonly status: number,
    url: string,
  ) {
    super(`console returned ${status} for ${url}`);
  }
}

// Every call carries the parent's session cookie (credentials: "include" —
// the console is cross-origin in dev, and its CORS opts into credentials).
async function request(path: string, init?: RequestInit): Promise<Response> {
  const url = `${apiOrigin()}/console/v1${path}`;
  const res = await fetch(url, { cache: "no-store", credentials: "include", ...init });
  if (res.status === 401) {
    // The session died mid-use (expiry, sign-out in another tab). AuthGuard
    // only sees it on its own session refetch (window focus), so the API
    // choke point routes to sign-in instead of stranding the parent on a
    // dead-end error screen. Never resolve: the page is navigating away, and
    // returning (or throwing) would flash callers' generic error UI first.
    window.location.replace("/sign-in");
    return new Promise<never>(() => {});
  }
  if (!res.ok) throw new ConsoleError(res.status, url);
  return res;
}

export async function fetchTurnsPage(deviceId: string, cursor?: string): Promise<TurnsPage> {
  const res = await request(
    `/devices/${deviceId}/turns` + (cursor ? `?${new URLSearchParams({ cursor })}` : ""),
  );
  // Media URLs (audio_url/image_url) arrive absolute and are used as-is —
  // the API owns where media lives, so a move to external storage or a CDN
  // never touches this client.
  return (await res.json()) as TurnsPage;
}

export async function fetchDevices(): Promise<LinkedDevice[]> {
  return (await (await request("/devices")).json()) as LinkedDevice[];
}

// The claim token is the device's UUID for now (prototype); production
// replaces it with a short-lived code the device speaks.
export async function linkDevice(deviceId: string, name: string): Promise<LinkedDevice> {
  const res = await request("/devices", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ device_id: deviceId, name }),
  });
  return (await res.json()) as LinkedDevice;
}

export async function renameDevice(deviceId: string, name: string): Promise<LinkedDevice> {
  const res = await request(`/devices/${deviceId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return (await res.json()) as LinkedDevice;
}

// Removes this account's access only — the device's history stays and
// re-linking restores it.
export async function unlinkDevice(deviceId: string): Promise<void> {
  await request(`/devices/${deviceId}`, { method: "DELETE" });
}
